import { observable, action, makeObservable, computed } from "mobx";
import {
    Notification,
    Damage,
    Location,
    Person,
    Nameplate,
    Key,
    NotificationImage,
    CONNECTION_TYPES,
    NOTIFICATION_TYPES
} from "src/api/Notification";
import { Process, Step, STEP_TYPES } from "src/api/Processes";
import { persist } from "mobx-persist";
import { SelectComponent, SELECT_COMPONENT_TYPES } from "src/api/Select";
import { RootStore } from "./RootStore";

const initialNotification: Notification = {
    version: "0.1",
    type: NOTIFICATION_TYPES.UNDEFINED,
    directsendready: false,
    abortdirectsend: false
};

const findPrevNode = (id: string, currentNode: SelectComponent): SelectComponent | null => {
    let result = null;
    if (currentNode.components) {
        result = currentNode.components.find((c: SelectComponent) => c.id === id) || null;
        if (result) {
            return currentNode;
        }
        for (let i = 0; i < currentNode.components.length; i += 1) {
            result = findPrevNode(id, currentNode.components[i]);
            if (result !== null) {
                return result;
            }
        }
    }
    return result;
};

const findNode = (id: string, currentNode: SelectComponent): SelectComponent | null => {
    let result = null;
    let currentChild: SelectComponent | undefined = undefined;
    if (id === currentNode.id) {
        return currentNode;
    } else {
        if (!currentNode.components) {
            return null;
        }
        for (let i = 0; i < currentNode.components.length; i += 1) {
            currentChild = currentNode.components[i];
            //Recursive function call!
            result = findNode(id, currentChild);
            if (result !== null) {
                return result;
            }
        }
        return null;
    }
};

export class EasyContactStore {
    rootStore: RootStore;
    @persist("object") @observable processes: Process[] | undefined = undefined;
    @persist("object") @observable selectComponent: SelectComponent[] | undefined = undefined;
    @persist("object") @observable notification: Notification = initialNotification;
    @persist("object") @observable backendContactInformation: Person | undefined = undefined;
    @persist("object") @observable backendAvailableContactPersons: Person[] | undefined = undefined;
    @persist("object") @observable sentNotificationHash: number | undefined = undefined;
    @persist("object") @observable sentNotificationToken: string | undefined = undefined;
    @persist @observable applicationLanguage: string | undefined = undefined;
    @persist @observable roomType: number | undefined = undefined;
    @persist @observable applianceType: number | undefined = undefined;
    @persist @observable showLogin: boolean | false | undefined = undefined;
    @observable forwardingTypesProcessing: boolean | false | undefined = undefined;
    @observable showProfileAppbarButton = false;
    @observable canForwardToYarowa = false;
    @observable canForwardToService7000 = false;
    @persist @observable stepType: STEP_TYPES | undefined = undefined;
    @persist @observable isLoggedIn: boolean | false | undefined = undefined;
    @observable streetId: string = "";

    @observable stepProgression: { currentStep: number; totalSteps: number } = {
        currentStep: 0,
        totalSteps: 0
    };

    @observable stepNavigation: {
        nextStep: Step | undefined;
        prevStep: Step | undefined;
        currentStepTitle: string | undefined;
    } = {
        nextStep: undefined,
        prevStep: undefined,
        currentStepTitle: undefined
    };

    @observable componentNavigation: { prevComponentId: string | undefined } = {
        prevComponentId: undefined
    };

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeObservable(this);
    }
    /* Processes and Steps actions */
    @action
    setProcesses = (payload: Process[]) => {
        this.processes = payload;
    };

    getProcessById = (id: string) => {
        return this.processes?.find((process) => process.id === id);
    };

    getStepById = (processId: string, stepId: string) => {
        const process = this.getProcessById(processId);
        return process?.steps.find((step) => step.id === stepId);
    };

    @action
    setCurrentStep = (processId: string, stepId: string) => {
        // Enters Step in a Process
        const process = this.getProcessById(processId);
        const stepIndex = process?.steps.findIndex((step) => step.id === stepId) ?? -1;
        if (process && stepIndex !== -1) {
            // get neighboring Steps if available
            const currentStep = process.steps[stepIndex];
            const nextStep = stepIndex + 1 < process.steps.length ? process.steps[stepIndex + 1] : undefined;
            const prevStep = stepIndex >= 1 ? process.steps[stepIndex - 1] : undefined;
            this.stepNavigation = {
                nextStep,
                prevStep,
                currentStepTitle:
                    currentStep.step_mls.find((component) => component.language === this.applicationLanguage)?.title ||
                    ""
            };
            this.stepProgression = {
                totalSteps: process.steps.length,
                currentStep: stepIndex + 1
            };
        }
    };

    /* Notification Actions */
    @action
    setNewNotification = () => {
        this.notification = initialNotification;
        this.applianceType = undefined;
        this.roomType = undefined;
        this.sentNotificationHash = undefined;
        this.sentNotificationToken = undefined;
    };

    @action
    setNotificationtype = (notificationtype: number) => {
        this.notification.type = notificationtype;
    };

    @action
    setMessage = (message: string) => {
        this.notification.message = message;
    };

    @action
    setDocumentQuestion = (message: string, documentimage?: NotificationImage) => {
        this.notification.message = message;
        this.notification.documentimage = documentimage;
    };

    @action
    setComponent = (componentid: string, component: string) => {
        this.notification.componentid = componentid;
        this.notification.component = component;
    };

    @action
    setLocation = (payload: Location) => {
        this.notification = { ...this.notification, location: payload };
    };

    @action
    setNameplate = (payload: Nameplate) => {
        this.notification = { ...this.notification, nameplate: payload };
    };

    @action
    setKey = (payload: Key) => {
        this.notification = { ...this.notification, key: payload };
    };

    @action
    setDamage = (payload: Damage) => {
        this.notification = { ...this.notification, damage: payload };
    };

    @action
    setSendingPerson = (payload: Person) => {
        this.notification = { ...this.notification, contactperson: payload };
    };

    @action
    setBackendContactInformation = (contactInformation: Person | undefined) => {
        this.backendContactInformation = contactInformation;
    };

    @action
    setBackendAvailableContactPersons = (availableContactPersons: Person[] | undefined) => {
        this.backendAvailableContactPersons = availableContactPersons;
    };

    @action
    setSentNotificationHash = (sentNotificationHash: number | undefined) => {
        this.sentNotificationHash = sentNotificationHash;
    };

    @action
    setSentNotificationToken = (sentNotificationToken: string | undefined) => {
        this.sentNotificationToken = sentNotificationToken;
    };

    @action
    setRoomType = (roomType: number | undefined) => {
        this.roomType = roomType;
    };

    @action
    setApplianceType = (applianceType: number | undefined) => {
        this.applianceType = applianceType;
    };

    /* Component Actions */
    @action
    setSelectComponent = (payload: SelectComponent[]) => {
        this.selectComponent = payload;
    };

    @computed get getParentOfCurrentComonentId() {
        if (this.notification && this.notification.componentid && this.selectComponent) {
            let result = null;

            const selectedComponent = this.getSelectComponentById(this.notification.componentid);

            if (selectedComponent?.type === SELECT_COMPONENT_TYPES.OTHER) {
                return selectedComponent.id;
            } else {
                for (let component of this.selectComponent) {
                    result = findPrevNode(this.notification.componentid, component)?.id;
                    if (result) {
                        return result;
                    }
                }
            }
            return result;
        } else {
            return undefined;
        }
    }

    @action
    setForwardingType = (forwardingType: CONNECTION_TYPES | undefined) => {
        this.notification.forwardingtype = forwardingType;
    };

    @action
    setDirectSendReady = (isReady: boolean) => {
        this.notification.directsendready = isReady;
    };

    @action
    setAbortDirectSend = (isReady: boolean) => {
        this.notification.abortdirectsend = isReady;
    };

    @action
    setSelectComponentNavivgationPrevId = (componentId: string | undefined) => {
        this.componentNavigation.prevComponentId = componentId;
    };

    getSelectComponentById = (componentId: string): SelectComponent | null => {
        let result = null;
        if (this.selectComponent) {
            for (let component of this.selectComponent) {
                result = findNode(componentId, component);
                if (result) {
                    this.setSelectComponentNavivgationPrevId(findPrevNode(componentId, component)?.id);
                    break;
                }
            }
        }
        return result;
    };

    @action
    setApplicationLanguage = (applicationLanguage: string | undefined) => {
        this.applicationLanguage = applicationLanguage;
    };

    @action
    setShowLogin = (showLogin: boolean) => {
        this.showLogin = showLogin;
    };

    @action
    setShowProfileAppbarButton = (showProfileAppbarButton: boolean) => {
        this.showProfileAppbarButton = showProfileAppbarButton;
    };

    @action
    setForwardingTypesProcessing = (forwardingTypesProcessing: boolean) => {
        this.forwardingTypesProcessing = forwardingTypesProcessing;
    };

    @action
    setCanForwardToYarowa = (canForwardToYarowa: boolean) => {
        this.canForwardToYarowa = canForwardToYarowa;
    };

    @action
    setCanForwardToService7000 = (canForwardToService7000: boolean) => {
        this.canForwardToService7000 = canForwardToService7000;
    };

    @action
    setStepType = (stepType: STEP_TYPES) => {
        this.stepType = stepType;
    };

    @action
    setIsLoggedIn = (isLoggedIn: boolean) => {
        this.isLoggedIn = isLoggedIn;
    };

    @action
    setStreetId = (streetId: string) => {
        this.streetId = streetId;
    };
}
